import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { ThemeToggle } from '../../components/ThemeToggle';
import { Breadcrumbs } from '../../components/navigation/NavigationHelpers';
import { TypeSpecificFields } from '../../components/commerce/TypeSpecificFields';
import { 
  Store, Package, ShoppingBag, DollarSign, 
  TrendingUp, Plus, Edit2, Trash2, Eye, ChevronLeft 
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function MerchantDashboard() {
  const [store, setStore] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [productForm, setProductForm] = useState({
    name: '',
    description: '',
    price: '',
    currency: 'ZAR',
    stock: '',
    category: '',
    type_specific_data: {}
  });
  const navigate = useNavigate();

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const [storeRes, productsRes, ordersRes] = await Promise.all([
        fetch(`${API_URL}/api/commerce/stores/my-store`, { credentials: 'include' }),
        fetch(`${API_URL}/api/commerce/products/my-products`, { credentials: 'include' }),
        fetch(`${API_URL}/api/commerce/orders/merchant-orders`, { credentials: 'include' })
      ]);

      if (storeRes.ok) {
        const data = await storeRes.json();
        if (!data.has_store) {
          navigate('/commerce/create-store');
          return;
        }
        setStore(data);
      }

      if (productsRes.ok) {
        const data = await productsRes.json();
        setProducts(data.products || []);
      }

      if (ordersRes.ok) {
        const data = await ordersRes.json();
        setOrders(data.orders || []);
      }
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    }
  };

  const handleAddProduct = async (e) => {
    e.preventDefault();

    try {
      const res = await fetch(`${API_URL}/api/commerce/products/create`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...productForm,
          price: parseFloat(productForm.price),
          stock: parseInt(productForm.stock) || null
        })
      });

      if (res.ok) {
        alert('Product added!');
        setShowAddProduct(false);
        setProductForm({ name: '', description: '', price: '', currency: 'ZAR', stock: '', category: '', type_specific_data: {} });
        loadDashboard();
      } else {
        alert('Failed to add product');
      }
    } catch (error) {
      alert('Failed to add product');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Delete this product?')) return;

    try {
      const res = await fetch(`${API_URL}/api/commerce/products/${productId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (res.ok) {
        alert('Product deleted');
        loadDashboard();
      }
    } catch (error) {
      alert('Failed to delete product');
    }
  };

  if (!store) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  const pendingOrders = orders.filter(o => o.status === 'paid' || o.status === 'processing');

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Theme Toggle */}
      <div className="fixed top-4 right-4 z-50">
        <ThemeToggle size="icon" className="bg-white dark:bg-gray-800 shadow-lg" />
      </div>

      <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-6">
        {/* Back Button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/commerce/marketplace')}
          className="mb-4"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Back to Marketplace
        </Button>

        {/* Breadcrumbs */}
        <Breadcrumbs items={[
          { label: 'Commerce', href: '/commerce/marketplace' },
          { label: 'My Store' }
        ]} />

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Store className="w-8 h-8 text-blue-500" />
              {store.store?.store_name}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">Merchant Dashboard</p>
          </div>
          <Button onClick={() => navigate('/commerce/marketplace')}>
            <Eye className="w-4 h-4 mr-2" />
            View Store
          </Button>
        </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Products</p>
                <p className="text-2xl font-bold">{products.length}</p>
              </div>
              <Package className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Pending Orders</p>
                <p className="text-2xl font-bold">{pendingOrders.length}</p>
              </div>
              <ShoppingBag className="w-8 h-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Sales</p>
                <p className="text-2xl font-bold">{store.store?.total_sales || 0}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Revenue</p>
                <p className="text-2xl font-bold">{store.store?.total_revenue || 0}</p>
              </div>
              <DollarSign className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Products */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Products</CardTitle>
            <Button onClick={() => setShowAddProduct(!showAddProduct)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Product
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {showAddProduct && (
            <form onSubmit={handleAddProduct} className="mb-6 p-4 border rounded-lg space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <Input
                  required
                  placeholder="Product Name"
                  value={productForm.name}
                  onChange={(e) => setProductForm({...productForm, name: e.target.value})}
                />
                <div className="flex gap-2">
                  <Input
                    required
                    type="number"
                    step="0.01"
                    placeholder="Price"
                    value={productForm.price}
                    onChange={(e) => setProductForm({...productForm, price: e.target.value})}
                  />
                  <select
                    value={productForm.currency}
                    onChange={(e) => setProductForm({...productForm, currency: e.target.value})}
                    className="px-3 py-2 border rounded-lg"
                  >
                    <option value="ZAR">ZAR</option>
                    <option value="MTR">MTR</option>
                    <option value="USDC">USDC</option>
                    <option value="USDT">USDT</option>
                  </select>
                </div>
              </div>
              <textarea
                required
                placeholder="Description"
                value={productForm.description}
                onChange={(e) => setProductForm({...productForm, description: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
                rows="3"
              />
              <div className="grid md:grid-cols-2 gap-4">
                <Input
                  placeholder="Category"
                  value={productForm.category}
                  onChange={(e) => setProductForm({...productForm, category: e.target.value})}
                />
                <Input
                  type="number"
                  placeholder="Stock (optional)"
                  value={productForm.stock}
                  onChange={(e) => setProductForm({...productForm, stock: e.target.value})}
                />
              </div>
              
              {/* Type-Specific Fields Component */}
              {store?.store?.store_type && (
                <TypeSpecificFields 
                  storeType={store.store.store_type}
                  formData={productForm}
                  setFormData={setProductForm}
                />
              )}
              
              <div className="flex gap-2">
                <Button type="submit">Add Product</Button>
                <Button type="button" variant="outline" onClick={() => setShowAddProduct(false)}>Cancel</Button>
              </div>
            </form>
          )}

          {products.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No products yet. Add your first product!</p>
          ) : (
            <div className="space-y-3">
              {products.map(product => (
                <div key={product.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                  <div className="flex-1">
                    <h3 className="font-semibold">{product.name}</h3>
                    <p className="text-sm text-gray-600 line-clamp-1">{product.description}</p>
                    <div className="flex items-center gap-4 mt-2 text-sm">
                      <Badge>{product.price} {product.currency}</Badge>
                      <span className="text-gray-500">{product.sales_count} sales</span>
                      <Badge variant={product.status === 'active' ? 'default' : 'secondary'}>{product.status}</Badge>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline">
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleDeleteProduct(product.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Orders */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Orders</CardTitle>
        </CardHeader>
        <CardContent>
          {orders.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No orders yet</p>
          ) : (
            <div className="space-y-3">
              {orders.slice(0, 10).map(order => (
                <div key={order.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <p className="font-semibold">Order #{order.id.substring(0, 8)}</p>
                    <p className="text-sm text-gray-600">{order.customer_name}</p>
                    <p className="text-sm text-gray-500">{new Date(order.created_at).toLocaleDateString()}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold">{order.order_total} {order.currency}</p>
                    <Badge>{order.status}</Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
    </div>
  );
}
